import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, makeStyles } from '@material-ui/core'
import { Link, useHistory } from 'react-router-dom'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import OperationalTable, { useOperationalTable } from '../../../../organisms/OperationalTable'
import { ADMIN_ROUTES } from '../../../../../constants'
import { useSearchAccountsForRegistration } from '../../../../../api/registrations'

dayjs.extend(utc)

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: '0.5rem 0 1rem 0'
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center',
    color: 'var(--color-text-tertiary)'
  }
}))

const formatDate = (value) =>
  value ? dayjs.utc(value).format('MM/DD/YYYY') : '—'

const accountNameCell = ({ row }) => {
  const account = row.original
  return (
    <Link
      onClick={(e) => e.stopPropagation()}
      to={`${ADMIN_ROUTES.ACCOUNTS}/${account.accountId}`}
    >
      {account.accountName || account.longName || `Account ${account.accountId}`}
    </Link>
  )
}

const dateCell = (field) => ({ row }) => formatDate(row.original[field])

const columnConfig = {
  columns: [
    {
      Header: 'Account Name',
      accessor: 'accountName',
      id: 'accountName',
      Cell: accountNameCell
    },
    {
      Header: 'Account Number',
      accessor: 'accountNumber',
      id: 'accountNumber'
    },
    {
      Header: 'Account Type',
      accessor: 'accountTypeId',
      id: 'accountTypeId',
      disableSortBy: true
    },
    {
      Header: 'Registration',
      accessor: 'accountRegistration',
      id: 'accountRegistration',
      disableSortBy: true
    },
    {
      Header: 'Orion Account ID',
      accessor: 'orionAccountId',
      id: 'orionAccountId',
      disableSortBy: true
    },
    {
      Header: 'Modified',
      accessor: 'modifiedDate',
      id: 'modifiedDate',
      Cell: dateCell('modifiedDate')
    }
  ],
  defaultSort: [
    { id: 'accountName', desc: false }
  ]
}

const buildBody = ({ pageIndex, pageSize, sort }) => {
  const body = {
    skip: pageIndex * pageSize,
    take: pageSize
  }
  if (sort && sort.length > 0) {
    body.sort = sort.map(({ id, desc }) => ({
      field: id,
      dir: desc ? 'desc' : 'asc'
    }))
  }
  return body
}

function AccountsAssignedTab ({ orionFirmId, orionRegistrationId }) {
  const classes = useStyles()
  const history = useHistory()
  const {
    defaultPageSize,
    pageIndex,
    pageSize,
    sort,
    onPagingChange,
    onSortingChange,
    onTableModeChange
  } = useOperationalTable(columnConfig)

  const body = useMemo(
    () => buildBody({ pageIndex, pageSize, sort }),
    [pageIndex, pageSize, sort]
  )

  const { data: result, isFetching } = useSearchAccountsForRegistration(
    orionFirmId,
    orionRegistrationId,
    body
  )

  const rows = result?.data ?? []
  const total = result?.total ?? 0

  const onClickRow = useCallback((row) => {
    if (row.original?.accountId != null) {
      history.push(`${ADMIN_ROUTES.ACCOUNTS}/${row.original.accountId}`)
    }
  }, [history])

  return (
    <Box className={classes.panel}>
      <OperationalTable.Wrapper>
        <OperationalTable
          mode='server'
          columns={columnConfig.columns}
          data={rows}
          defaultPageSize={defaultPageSize}
          defaultSort={columnConfig.defaultSort}
          itemName='Accounts'
          loading={isFetching}
          total={total}
          onSortingChange={onSortingChange}
          onPagingChange={onPagingChange}
          onTableModeChange={onTableModeChange}
          onRowClick={onClickRow}
        />
      </OperationalTable.Wrapper>
      {!isFetching && total === 0 ? (
        <div className={classes.emptyState}>
          No accounts are currently linked to this registration.
        </div>
      ) : null}
    </Box>
  )
}

AccountsAssignedTab.propTypes = {
  orionFirmId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  orionRegistrationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

export default AccountsAssignedTab
